import { Link, useParams } from "react-router-dom";
import type { CallJourney } from "@starsach/shared-types";
import { useCallJourney } from "../api/hooks";
import { Card, ErrorState, LoadingState, NotAvailable, PageHeader, RelativeTime, StatusPill } from "../components/ui";
import { useFormat } from "../lib/useFormat";

/**
 * design.md "View 2 - Single-call view": the spine renders as soon as
 * the contact chain and flow trace are in; each other panel fills in as
 * its source completes, or says why it can't.
 */
export function CallJourneyPage() {
  const { environmentId = "", contactId = "" } = useParams();
  const q = useCallJourney(environmentId, contactId);
  if (q.isPending) return <LoadingState label="Starting call reconstruction" />;
  if (q.isError) return <ErrorState error={q.error} onRetry={() => void q.refetch()} />;
  const j = q.data;

  return (
    <>
      <PageHeader
        title="Call journey"
        subtitle={`Contact ${contactId}`}
        actions={
          <div className="flex items-center gap-3 text-sm">
            <StatusPill status={j.status} tone={j.status === "complete" ? "ok" : j.status === "failed" ? "critical" : "warning"} />
            <Link to={`/customers/${environmentId}/journeys`} className="underline">Back to journeys</Link>
          </div>
        }
      />
      {j.status === "assembling" && <p role="status" aria-live="polite" className="mb-4 text-sm text-neutral-600 dark:text-neutral-400">Assembling from the customer's account - flow logs, Lambda and Lex logs, Contact Lens and the recording are queried in parallel. Panels fill in as they complete.</p>}
      {j.status === "failed" && <div role="alert" className="mb-4 rounded border border-red-300 bg-red-50 p-3 text-sm text-red-900 dark:border-red-900 dark:bg-red-950 dark:text-red-100">Reconstruction failed: {j.error ?? "unknown error"}. Check the customer's JourneyInspectorRole and declared log groups.</div>}

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="grid gap-6 lg:col-span-2">
          <Legs legs={j.legs ?? []} />
          <Timeline t={j.timeline} />
          <Panel title="Lambda invocations" p={j.lambdas}>
            {j.lambdas?.invocations.map((inv, i) => (
              <details key={i} className="mb-2 rounded border border-neutral-200 p-2 dark:border-neutral-800">
                <summary className="cursor-pointer text-sm"><span className="font-mono">{inv.functionName}</span> · <RelativeTime iso={inv.timestamp} />{inv.durationMs !== undefined && <> · {inv.durationMs} ms</>}</summary>
                <pre className="mt-2 max-h-64 overflow-auto whitespace-pre-wrap font-mono text-xs">{inv.logLines.join("\n")}</pre>
              </details>
            ))}
          </Panel>
          <Panel title="Data dips (CRM / third-party lookups)" p={j.dataDips}>
            {j.dataDips?.items.map((d, i) => (
              <div key={i} className="mb-2 grid gap-2 text-xs md:grid-cols-2">
                <pre className="overflow-auto rounded bg-neutral-100 p-2 dark:bg-neutral-800"><span className="font-semibold">{d.functionName} request</span>{"\n"}{JSON.stringify(d.request, null, 2)}</pre>
                <pre className="overflow-auto rounded bg-neutral-100 p-2 dark:bg-neutral-800"><span className="font-semibold">response</span>{"\n"}{JSON.stringify(d.response, null, 2)}</pre>
              </div>
            ))}
          </Panel>
        </div>
        <div className="grid gap-6">
          <Panel title="Bot" p={j.bot}>
            <ol className="space-y-2 text-sm">
              {j.bot?.turns.map((t, i) => (
                <li key={i} className="rounded border border-neutral-200 p-2 dark:border-neutral-800">
                  <p className="text-xs text-neutral-500"><RelativeTime iso={t.timestamp} /> · {t.intent ?? "no intent"}{t.fallback && <span className="ml-1 text-amber-700 dark:text-amber-300">fallback</span>}</p>
                  {t.utterance && <p className="mt-1">“{t.utterance}”</p>}
                  {t.slots && Object.keys(t.slots).length > 0 && <p className="mt-1 font-mono text-xs">{Object.entries(t.slots).map(([k, v]) => `${k}=${v}`).join(" ")}</p>}
                </li>
              ))}
            </ol>
          </Panel>
          <Panel title="Agent transcript (Contact Lens)" p={j.agent}>
            {j.agent && j.agent.categories.length > 0 && <p className="mb-2 flex flex-wrap gap-1">{j.agent.categories.map((c) => <StatusPill key={c} status={c} tone="neutral" />)}</p>}
            <ol className="space-y-1 text-sm">
              {j.agent?.transcript.map((t, i) => (
                <li key={i} className={t.participant === "AGENT" ? "text-accent-strong" : ""}>
                  <span className="font-mono text-xs text-neutral-500">{(t.beginOffsetMs / 1000).toFixed(0)}s</span> <span className="font-medium">{t.participant}</span>: {t.content}
                  {t.sentiment && t.sentiment !== "NEUTRAL" && <span className="ml-1 text-xs text-neutral-500">({t.sentiment.toLowerCase()})</span>}
                </li>
              ))}
            </ol>
          </Panel>
          <Panel title="Recording" p={j.recording}>
            {j.recording?.url && (
              <>
                {/* eslint-disable-next-line jsx-a11y/media-has-caption -- a captions track is always present below; the rule cannot see through the conditional */}
                <audio controls className="w-full" aria-label="Call recording">
                  <source src={j.recording.url} />
                  {/* The Contact Lens transcript doubles as the captions track when it exists. */}
                  {j.agent?.status === "ok" && j.agent.transcript.length > 0 ? (
                    <track kind="captions" srcLang="en" label="Transcript" src={vttFrom(j.agent.transcript)} default />
                  ) : (
                    <track kind="captions" srcLang="en" label="No transcript available" src="data:text/vtt,WEBVTT" />
                  )}
                  Your browser cannot play this recording.
                </audio>
                <p className="mt-1 text-xs text-neutral-500">Link expires <RelativeTime iso={j.recording.expiresAt} />.{j.agent?.status !== "ok" && " No transcript is available for captions."}</p>
              </>
            )}
          </Panel>
        </div>
      </div>
    </>
  );
}

function Panel({ title, p, children }: { title: string; p: { status: "ok" | "not_available" | "error"; note?: string } | undefined; children: React.ReactNode }) {
  return (
    <Card title={title}>
      {!p || p.status === "not_available" && !p.note ? <LoadingState label="Waiting" /> : null}
      {p?.status === "not_available" && p.note && <NotAvailable what={title} why={p.note} />}
      {p?.status === "error" && <ErrorState error={{ message: p.note ?? "Source failed" }} />}
      {p?.status === "ok" && children}
    </Card>
  );
}

function Legs({ legs }: { legs: CallJourney["legs"] }) {
  const fmt = useFormat();
  if (legs.length === 0) return <Card title="Contact"><LoadingState label="Reading the contact record" /></Card>;
  return (
    <Card title={legs.length > 1 ? `Transfer chain (${legs.length} legs)` : "Contact"}>
      <ol className="space-y-2 text-sm">
        {legs.map((l, i) => (
          <li key={l.contactId} className="rounded border border-neutral-200 p-2 dark:border-neutral-800">
            <p className="font-mono text-xs text-neutral-500">{i + 1}. {l.contactId}{l.previousContactId && <> ← {l.previousContactId}</>}</p>
            <p className="mt-1">{l.initiationMethod ?? "?"} {l.channel ?? ""} · queue {l.queueName ?? "-"} · agent {l.agentId ?? "none"}</p>
            <p className="text-neutral-600 dark:text-neutral-400">{l.customerEndpoint ?? "?"} → {l.systemEndpoint ?? "?"} · {l.initiationTimestamp ? fmt.when(l.initiationTimestamp) : "?"}{l.disconnectTimestamp && <> → {fmt.time(l.disconnectTimestamp)}</>} · {l.disconnectReason ?? ""}</p>
          </li>
        ))}
      </ol>
    </Card>
  );
}

function Timeline({ t }: { t: CallJourney["timeline"] | undefined }) {
  const fmt = useFormat();
  return (
    <Panel title="Flow timeline" p={t}>
      <ol className="relative space-y-0 border-l-2 border-neutral-200 pl-4 text-sm dark:border-neutral-700">
        {t?.steps.map((s, i) => (
          <li key={i} className={`py-1.5 ${s.error ? "text-red-700 dark:text-red-300" : ""}`}>
            <span className="font-mono text-xs text-neutral-500">{fmt.time(s.timestamp)}</span>{" "}
            <span className="font-medium">{s.identifier ?? s.moduleType}</span>
            <span className="text-neutral-500"> · {s.moduleType}{s.flowName ? ` · ${s.flowName}` : ""}</span>
            {s.dwellMs !== undefined && <span className="text-neutral-500"> · {(s.dwellMs / 1000).toFixed(1)}s</span>}
            {s.error && <span className="ml-1 font-semibold">error</span>}
          </li>
        ))}
      </ol>
    </Panel>
  );
}

/** WebVTT from Contact Lens turns; each cue runs until the next turn starts. */
function vttFrom(transcript: NonNullable<CallJourney["agent"]>["transcript"]): string {
  const ts = (ms: number) => new Date(ms).toISOString().slice(11, 23);
  const cues = transcript.map((t, i) => {
    const end = transcript[i + 1]?.beginOffsetMs ?? t.beginOffsetMs + 4000;
    return `${ts(t.beginOffsetMs)} --> ${ts(Math.max(end, t.beginOffsetMs + 500))}\n${t.participant}: ${t.content.replace(/\n/g, " ")}`;
  });
  return `data:text/vtt;charset=utf-8,${encodeURIComponent(`WEBVTT\n\n${cues.join("\n\n")}`)}`;
}
